// ─── WHO IS WHO ─────────────────────────────────────────────────────────────
//
// Haley, live 2026-08-09:
//
//   "the ai keeps thinking that nicole is haley and characters use the
//    nicknames that are supposed to be for my characters and they keep saying
//    her bday is coming up which is right but they keep saying haley's bday
//    even though it's nicole's bday on august 15th please make sure that our
//    details are all separate and all characters that belong to us are like
//    assigned to us so that can't happen anywhere in the app but especially in
//    group dms"
//
// WHY THE MODEL KEEPS MERGING THEM. Three things stack up:
//
//  1. Their characters carry their own real names. `haley_human` and
//     `nicole_human` are both records, Haley writes "Haley admits, in a soft
//     whisper" in the third person, and Nicole's persona in several channels is
//     literally "Nicole". So "[Haley]:" in a transcript is ambiguous between
//     the woman and the character, and every line of both is first-person prose
//     about a woman with that name.
//  2. Nothing in the prompt ever said the two are different people with
//     different casts. The transcript labels each line, but a label is a weak
//     signal against 400 messages of blended narration, and the anchor named
//     only the player currently talking.
//  3. The birthday block used to list BOTH players and then instructed the
//     character to bring up the other one's birthday, by name. See
//     userBirthdays.js — that one is fixed there.
//
// The app already knew the answer: `channel_member_profiles.display_name` is
// the persona each writer plays per channel. This file makes that knowledge
// explicit and hands it to the model, instead of hoping it infers ownership
// from prose.
//
// Hardcoded for the two-user platform, exactly like userBirthdays.js. The
// rosters below were read off live data, not guessed:
//   select user_id, string_agg(distinct display_name, ' | ')
//     from channel_member_profiles group by user_id;

export const HALEY_ID = '6db164e7-0419-470c-a8fe-2ddb899f5578';
export const NICOLE_ID = '8420ef71-0898-41e0-ac6c-6916969b82f2';

export const PLAYERS = {
  [HALEY_ID]: {
    name: 'Haley',
    // Personas she writes. `Haley` is her own character, not merely her account.
    // ⚠️ ROUTE-GATED IDENTITIES ARE NOT LISTED HERE. Atropos was in this list
    // and should never have been: identityGates.js exists specifically to keep
    // the link between a mortal character and a Fate out of prompts, and this
    // block is in the CACHED half of EVERY character's prompt. Naming it here
    // told every character in the app what the gate is there to withhold —
    // and worse, because Haley's character is also called Haley, the model
    // could read "Haley writes Atropos" as "Haley IS Atropos", which is the
    // reveal itself. Being one of the three Fates is public; WHO is which is
    // not. The block separates two writers perfectly well without it.
    characters: ['Haley', 'Cruz', 'Ford Steele', 'Kya', 'Juliette'],
  },
  [NICOLE_ID]: {
    name: 'Nicole',
    characters: ['Nicole', 'Melody', 'Evelyn (Evie)', 'Malakai', 'Grandmother'],
  },
};

/** The other player, given one. Returns null if the id is unknown. */
export function otherPlayer(viewerUserId) {
  if (viewerUserId === HALEY_ID) return PLAYERS[NICOLE_ID];
  if (viewerUserId === NICOLE_ID) return PLAYERS[HALEY_ID];
  return null;
}

/**
 * The identity block. Goes in the STABLE half of the anchor: the viewer does
 * not change within a session, so this is byte-identical turn to turn and
 * costs nothing after the first call.
 *
 * Returns '' for an unknown viewer rather than guessing — naming the wrong
 * woman is the exact failure this exists to prevent, and silence is better.
 */
/**
 * THE GHOST-WRITER'S HALF OF WHO-IS-WHO (2026-09-23).
 *
 * Haley: "I asked draft not to draft as nicole and it did draft as her
 * character it didn't follow instructions/read context."
 *
 * buildWhoIsWhoBlock below is written for a CHARACTER speaking TO a player
 * ("the person writing to you right now is …"), so it was only ever wired
 * into characterIdentity.js. The draft ghost-writer is the other shape: it
 * writes AS the player, and it was shipped with no ownership fact at all —
 * its prompt said "one of them plays the female lead and the other writes the
 * love interests" and then never said WHICH, so the model inferred ownership
 * from whichever character dominated the last 40 messages. When that was
 * Nicole's, it drafted as Nicole's.
 *
 * Same rosters, same single source of truth, aimed the other way.
 */
export function buildGhostWriterOwnershipBlock(viewerUserId, { inPlayMine = [], inPlayTheirs = [], inPlayAI = [] } = {}) {
  const me = PLAYERS[viewerUserId];
  const them = otherPlayer(viewerUserId);
  if (!me || !them) return '';

  // THIS CHAT'S CAST, not the whole roster (Haley, 2026-09-23: "it should be
  // for our used characters in the chat only"). The roster says what she MAY
  // write anywhere; these are the ones she is actually writing here, read off
  // who wrote which name in the last messages. Naming them stops the auto
  // draft reaching for one of her other characters who is nowhere in the
  // scene, and names the other writer's character concretely instead of
  // leaving the model to work it out from the transcript.
  // Characters the APP is playing in this scene. They are nobody's to write in
  // a draft either, and until 2026-10-02 nothing in this block mentioned them —
  // it only ever divided the cast between the two writers, so in a channel
  // where the other writer is absent the model was told nothing at all about
  // the voices it was actually reading.
  const aiLine = inPlayAI.length ? `
- ${inPlayAI.join(', ')} ${inPlayAI.length > 1 ? 'are' : 'is'} played by the app, not by ${me.name} and not by ${them.name}. ${inPlayAI.length > 1 ? 'They answer' : 'That character answers'} on ${inPlayAI.length > 1 ? 'their' : 'its'} own after this draft is sent. The draft may speak TO ${inPlayAI.length > 1 ? 'them' : 'that character'} and react to what ${inPlayAI.length > 1 ? 'they have' : 'it has'} already said, and must never write ${inPlayAI.length > 1 ? 'their' : 'its'} next line.` : '';

  const scene = (inPlayMine.length || inPlayTheirs.length || inPlayAI.length)
    ? `

IN THIS CHAT SPECIFICALLY — this is the part that governs:${inPlayMine.length ? `
- ${me.name} has been writing: ${inPlayMine.join(', ')}. The draft speaks as ${inPlayMine.length > 1 ? 'these' : 'this one'} and no one else. Do not pull in another of ${me.name}'s characters who is not in this scene.` : ''}${inPlayTheirs.length ? `
- ${them.name} has been writing: ${inPlayTheirs.join(', ')}. ${inPlayTheirs.length > 1 ? 'They are' : 'That character is'} the other writer's. ${inPlayTheirs.length > 1 ? 'They' : 'She or he'} may be looked at, spoken to, touched and reacted to — but never given a line, an action, a thought or a reaction of ${inPlayTheirs.length > 1 ? 'their' : 'their'} own in this draft.` : ''}${aiLine}`
    : '';

  return `

═══ WHOSE CHARACTERS THESE ARE — the draft is ${me.name}'s turn, not ${them.name}'s ═══
You are ghost-writing FOR ${me.name}. There are exactly two writers here and the cast is divided:
- ${me.name} writes: ${me.characters.join(', ')}. These are the voices this draft may speak in.
- ${them.name} writes: ${them.characters.join(', ')}. These are NOT yours to write, in this draft or any other.${scene}

HARD RULES:
- NEVER write ${them.name}'s characters' dialogue, actions, thoughts, decisions or reactions. They may be present, addressed, and reacted TO — the draft simply stops where their answer would begin.
- Do not answer on their behalf, do not narrate what they feel, and do not describe them doing something that would be ${them.name}'s turn to write.
- Whoever spoke most in the recent messages does NOT decide this. Ownership is the list above, and the transcript never overrides it.
- If the scene seems to need ${them.name}'s character to act before ${me.name} can, write up to that point and stop there. A short draft that leaves them their turn is correct; taking their turn is not.`;
}

export function buildWhoIsWhoBlock(viewerUserId, { surface = '' } = {}) {
  const me = PLAYERS[viewerUserId];
  const them = otherPlayer(viewerUserId);
  if (!me || !them) return '';

  const groupNote = surface === 'group'
    ? ` This is a GROUP conversation, so more than one of them may be present at once — go by the [name] label on each line and never blend two speakers into one person.`
    : '';

  return `

═══ WHO YOU ARE TALKING TO — two different people, never merge them ═══
The person writing to you right now is ${me.name}. Address ${me.name} as ${me.name}.${groupNote}

There are exactly two writers on this platform and they are NOT the same person:
- ${me.name} — writes: ${me.characters.join(', ')}.
- ${them.name} — writes: ${them.characters.join(', ')}. ${them.name} is a DIFFERENT person and is not the one talking to you unless a line is labelled with ${them.name}'s name.

HARD RULES:
- Never call ${me.name} by the name ${them.name}, and never greet, thank, tease or address ${me.name} as though she were ${them.name}.
- A nickname, pet name, or endearment belongs to the ONE character it was established with. If you call ${them.name}'s character something, that name is hers alone — never use it for ${me.name}'s character, and never carry it across.
- Personal facts do not transfer between them: birthdays, families, jobs, homes, history, wounds, and everything either has told you in private. A fact you learned about one of them is NOT true of the other.
- Their characters' relationships are separate too. What your character is to ${them.name}'s character says nothing about what you are to ${me.name}'s.
- If you are ever unsure which of the two you are speaking to or about, say nothing rather than guess. Using the wrong name is far worse than leaving it out.`;
}
